import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { classNames } from "../../../utils/twMergeWithCNAndPrefix";
import type { BulletProp } from "./Bullet";
import { Bullet } from "./Bullet";
import { defaultStyles } from "./defaultStyles";

export interface ListItem {
  bullet?: BulletProp;
  bulletStyles?: Record<string, StyleValue>;
  id: string;
  url?: string;
  value: string;
}

interface UlProps extends ComponentPropsWithRef<"ul"> {
  items: ListItem[];
  styles?: Record<string, StyleValue>;
}

const hasCustomBullet = (bullet?: BulletProp) =>
  Boolean(bullet?.emoji || bullet?.signed_id || bullet?.placeholder);

export const Ul = forwardRef<HTMLUListElement, UlProps>(
  ({ items = [], styles = {}, className, ...rest }, ref) => {
    const inlineStyles = parseStyles({ styles, defaultStyles });

    return (
      <ul
        ref={ref}
        className={classNames(
          "wb-text-p1-color wb-list-disc wb-text-left wb-pt-2 wb-list-outside",
          className,
        )}
        style={inlineStyles}
        {...rest}
      >
        {items.map(({ id, bullet, value, url, bulletStyles = {} }) =>
          url ? (
            <li
              key={id}
              className={classNames("wb-mb-2", {
                "wb-pl-0 wb-list-none wb-flex wb-items-center":
                  hasCustomBullet(bullet),
              })}
            >
              <a
                href={url}
                className={classNames(
                  "wb-text-link wb-font-p1 wb-leading-p1 wb-tracking-p1 wb-space-x-2",
                  {
                    "wb-inline-flex wb-items-center": hasCustomBullet(bullet),
                  },
                )}
              >
                {bullet && <Bullet bullet={bullet} styles={bulletStyles} />}
                <span>{value}</span>
              </a>
            </li>
          ) : (
            <li
              key={id}
              className={classNames("wb-mb-2", {
                "wb-list-none": hasCustomBullet(bullet),
              })}
            >
              <div
                className={classNames("wb-inline-grid", {
                  "wb-gap-2 wb-items-start wb-grid-cols-[auto_1fr]":
                    hasCustomBullet(bullet),
                })}
              >
                {bullet && hasCustomBullet(bullet) && (
                  <Bullet bullet={bullet} styles={bulletStyles} />
                )}
                <p className="wb-text-inherit wb-font-p1 wb-text-p1 wb-leading-p1 wb-tracking-p1 wb-inline-flex">
                  {value}
                </p>
              </div>
            </li>
          ),
        )}
      </ul>
    );
  },
);

Ul.displayName = "Ul";
